import {Injectable} from '@angular/core';
import {NotificationPerBuilding} from '../model/notification-per-building';
import {catchError, delay, map, tap} from 'rxjs/internal/operators';
import {Notification} from '../model/notification';
import {Observable, of, Subject, throwError} from 'rxjs';
import {Message} from '../model/message';
import {Photo} from '../model/photo';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import { ApiRoutes } from 'src/app/config/api-routes';
import { NotificationDetails } from '../model/notification-details';
import {LoggerService} from '../../../service/logger.service';
import { PromotedIssueInfo } from '../model/promoted-issue-info';
import {Localization} from '../enums/localization';
import {IssueNotificationStatus, SortDirection} from '../enums/enums';
import { UpdateNotificationRequest } from '../model/update-notification-request';
import { DateHelper } from 'src/app/helpers/dateHelper';
import {PaginationResponse} from '../model/pagination-response';
import { toISOString } from 'src/app/helpers/dateFormatters';

@Injectable()
export class NotificationsService {

  constructor(private httpClient: HttpClient, private apiRoutes: ApiRoutes) {}

  public notificationUpdated$: Subject<any> = new Subject<any>();

  public getUnreadNotificationsMessages(): Observable<Message[]> {
    return this.httpClient.get<Message[]>(this.apiRoutes.MESSAGES, {
      params: {
        pageNumber: '1',
        pageSize: '5',
        isRead: 'false',
        groupByIssueNotification: 'true',
        onlyMessagesFromResident: 'true',
        sortDirection: SortDirection.DESC
      }
    }).pipe(
      catchError(error => {
        return of([]);
      })
    );
  }

  public getLatestNotificationsMessages(): Observable<Message[]> {
    return this.httpClient.get<Message[]>(this.apiRoutes.MESSAGES, {
      params: {
        pageNumber: '1',
        pageSize: '5',
        groupByIssueNotification: 'true',
        onlyMessagesFromResident: 'true',
        sortDirection: SortDirection.DESC
      }
    }).pipe(
      catchError(error => {
        return of([]);
      })
    );
  }

  public getNotificationsPerBuilding(): Observable<NotificationPerBuilding[]> {
    return this.httpClient.get<NotificationPerBuilding[]>(this.apiRoutes.NOTIFICATION_BY_BUILDINGS).pipe(
      catchError(error => {
        return of([]);
      })
    );
  }

  public getNotifications(httpParams: HttpParams, pageNumber: number, pageSize: number): Observable<PaginationResponse<Notification>> {
    const params = httpParams
      .append('pageNumber', pageNumber.toString())
      .append('pageSize', pageSize.toString());
    return this.httpClient.get<PaginationResponse<Notification>>(this.apiRoutes.NOTIFICATIONS, {params: params}).pipe(
      catchError(error => {
        return of(new PaginationResponse<Notification>([], -1, -1));
      })
    );
  }

  public getLatestNotifications(): Observable<PaginationResponse<Notification>> {
    return this.httpClient.get<PaginationResponse<Notification>>(this.apiRoutes.NOTIFICATIONS + '?pageSize=5&pageNumber=1').pipe(
      catchError(error => {
        return of(new PaginationResponse<Notification>([], -1, -1));
      })
    );
  }

  public getOpenNotifications(): Observable<PaginationResponse<Notification>> {
    return this.httpClient.get<PaginationResponse<Notification>>(this.apiRoutes.NOTIFICATIONS +
      `?pageSize=5&pageNumber=1&status=${IssueNotificationStatus.OPEN}`).pipe(
      catchError(error => {
        return of(new PaginationResponse<Notification>([], -1, -1));
      })
    );
  }

  public getNotification(notificationId: number): Observable<NotificationDetails> {
    return this.httpClient.get<NotificationDetails>(this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.NOTIFICATION_DETAILS, [notificationId.toString()]));
  }

  public getNotificationMedia(notificationId: number): Observable<Photo[]> {
    return this.httpClient.get<Photo[]>(this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.NOTIFICATION_MULTIMEDIA, [notificationId.toString()]));
  }

  public getNotificationMessages(notificationId: number): Observable<Message[]> {
    return this.httpClient.get<Message[]>(this.apiRoutes.MESSAGES, {
      params: {
        IssuesNotificationsIds: notificationId.toString(),
        pageNumber: '1',
        pageSize: '1000'
      }
    }).pipe(
      map(messages => messages.map(message => {
        message.createdOn = DateHelper.convertDotNetDateToUTCDate(message.createdOn).toString();
        return message;
      })),
      catchError(error => {
        return of([]);
      })
    );
  }

  public promoteToIssue(notification: NotificationDetails): Observable<PromotedIssueInfo> {
    return this.httpClient.post<PromotedIssueInfo>(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.NOTIFICATION_PROMOTE, [notification.id.toString()]), {}).pipe(
        catchError(error => {
          return throwError({});
        }),
        tap(() => this.notificationUpdated$.next(null))
      );
  }

  public withoutConsequence(issueNotificationId: number): Observable<any> {
    return this.httpClient.post(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.NOTIFICATION_WITHOUT_CONSEQUENCE, [issueNotificationId.toString()]), {})
      .pipe(
      catchError(error => {
        return of(true);
      })
    );
  }

  public changeStatusToOpen(issueNotificationId: number): Observable<any> {
    return this.httpClient.post(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.NOTIFICATION_OPEN, [issueNotificationId.toString()]), {})
      .pipe(
      catchError(error => {
        return of(true);
      })
    );
  }

  public createNotification(notification: Notification): Observable<number> {
    return this.httpClient.post<number>(this.apiRoutes.NOTIFICATIONS, notification).pipe(
      catchError(error => {
        return throwError(null);
      })
    );
  }

  public updateNotificationDetails(updateNotificationRequest: UpdateNotificationRequest): Observable<any> {
    return this.httpClient.put(this.apiRoutes.NOTIFICATIONS, updateNotificationRequest).pipe(
      catchError(error => {
        return throwError(null);
      }),
      tap(() => this.notificationUpdated$.next(null))
    );
  }

  public addNotificationMessage(issueNotificationId: number, message: string): Observable<any> {
    return this.httpClient.post<PromotedIssueInfo>(this.apiRoutes.NOTIFICATION_MESSAGE_ADD, {
      issueNotificationId: issueNotificationId,
      value: message,
      sentOn: toISOString(new Date()),
    }).pipe(
      catchError(error => {
        return of(null);
      }));
  }

  public deletePhoto(photoId: number, notificationId: number): Observable<any> {
    return this.httpClient.delete(this.apiRoutes.getRouteWithInjectedParams(
      this.apiRoutes.NOTIFICATION_MULTIMEDIUM, [notificationId.toString(), photoId.toString()])
    ).pipe(
      catchError(error => {
        return of(true);
      })
    );
  }

  public uploadPhoto(file: File, notificationId: number): Observable<any> {
    const httpUploadOptions = {headers: new HttpHeaders({ 'Accept': 'application/json' })};
    const formData = new FormData();
    formData.append('files', file, file.name);
    return this.httpClient.post(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.NOTIFICATION_MULTIMEDIA, [notificationId.toString()]),
      formData,
      httpUploadOptions
    ).pipe(
      delay(1000),
      catchError(error => {
        return of(true);
      })
    );
  }

  public markMessagesAsRead(issueNotificationId: number): Observable<any> {
    return this.httpClient.post(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.NOTIFICATION_MARK_AS_READ, [issueNotificationId.toString()]), {})
      .pipe(
      catchError(error => {
        return of(true);
      })
    );
  }

  public sendPhotosToIssue(issueId: number, issueNotificationMultimediaIds: number[], application: string): Observable<any> {
    return this.httpClient.post(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.NOTIFICATION_MULTIMEDIUM_SEND_TO_ISSUE, [issueId.toString()]),
      { issueNotificationMultimediaIds: issueNotificationMultimediaIds, application: application})
      .pipe(
      catchError(error => {
        return of(true);
      })
    );
  }
}
